import { useState, useRef, useEffect, useCallback } from "react";
import {
  X, Send, Loader2, Minimize2,
  Clock, AlertCircle, CheckCircle2, HelpCircle, BookOpen,
  ChevronRight, Download, MessageSquareReply
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${window.location.origin}/api`;

// ── Paid plans that have notes access ────────────────────────────────────────
const PAID_PLANS = ["lokesh_notes", "unlimited", "premium", "paid", "tier2_pro", "basic", "standard"];

// ── Notes intent keywords ─────────────────────────────────────────────────────
const isNotesIntent = (msg) => {
  const l = msg.toLowerCase();
  return l.includes("notes") || l.includes("pdf") || l.includes("study material") ||
         l.includes("padhna") || l.includes("padhai") || l.includes("notes nahi") ||
         l.includes("topic") || l.includes("material") || l.includes("notes open") ||
         l.includes("notes kaise") || l.includes("notes chahiye");
};

// ── Bold text renderer ─────────────────────────────────────────────────────────
function MessageText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function FloatingChatBot({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState("bot");

  // ── Bot state ──────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState([{
    role: "bot",
    content: "Namaste! Main **Lokesh Sir Team** hun.\n\nMain aapki help kar sakta hun:\n• Notes nahi khul rahi?\n• Access nahi mila?\n• Password bhool gaye?\n• Mock test ya descriptive test ki problem?",
    time: new Date(),
    action_type: "answer"
  }]);
  const [input, setInput] = useState("");
  const [botLoading, setBotLoading] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const botEndRef = useRef(null);

  // ── Admin reply state ──────────────────────────────────────────────────────
  const [adminReply, setAdminReply] = useState(null);

  // ── Notes flow state ───────────────────────────────────────────────────────
  const [notesFlow, setNotesFlow] = useState({
    active: false,
    step: null,  // 'category' | 'subject' | 'topic' | 'fetching'
    category: null,
    subject: null,
    subjects: [],
    topics: [],
  });

  // ── Support Form state ─────────────────────────────────────────────────────
  const [formMessage, setFormMessage] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => { botEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── Check for admin reply on chatbot open ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || adminReply) return;
    const phone = user?.mobile || "";
    const email = user?.email || "";
    if (!phone && !email) return;
    axios.get(`${API}/support/check-reply?phone=${phone}&email=${encodeURIComponent(email)}`)
      .then(res => {
        if (res.data.has_reply) {
          setAdminReply(res.data);
          // Mark as seen
          if (res.data.ticket_id) {
            axios.post(`${API}/support/mark-reply-seen/${res.data.ticket_id}`).catch(() => {});
          }
        }
      })
      .catch(() => {});
  }, [isOpen]);

  // ── Show admin reply as bot message when reply is found ────────────────────
  useEffect(() => {
    if (!adminReply) return;
    addBotMsg("bot",
      `**Lokesh Sir Team ka jawab:**\n\n${adminReply.admin_reply}`,
      { action_type: "admin_reply" }
    );
    setAdminReply(null);
  }, [adminReply]);

  // ── Message helpers ────────────────────────────────────────────────────────
  const addBotMsg = (role, content, extra = {}) => {
    setMessages(prev => [...prev, { role, content, time: new Date(), ...extra }]);
  };

  const markPickerHandled = (idx) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, handled: true } : m));
  };

  // ── Notes flow handlers ────────────────────────────────────────────────────
  const startNotesFlow = useCallback(() => {
    const examTypes = [
      { label: "TGT (Trained Graduate Teacher)", value: "tgt" },
      { label: "PGT (Post Graduate Teacher)", value: "pgt" },
      { label: "PRT (Primary Teacher)", value: "prt" },
      { label: "Non-Teaching Staff", value: "non_teaching" },
    ];
    setNotesFlow({ active: true, step: "category", category: null, subject: null, subjects: [], topics: [] });
    addBotMsg("bot", "Kaunsa exam category ke notes chahiye?", {
      action_type: "notes_picker",
      flow_step: "category",
      options: examTypes.map(e => e.label),
      option_values: examTypes.map(e => e.value),
    });
  }, []);

  const handleCategorySelect = useCallback(async (value, label, msgIdx) => {
    markPickerHandled(msgIdx);
    addBotMsg("user", label);
    setBotLoading(true);
    try {
      const res = await axios.get(`${API}/subjects?exam_type=${value}`);
      const subjects = res.data.subjects || [];
      setNotesFlow(prev => ({ ...prev, step: "subject", category: value, subjects }));
      addBotMsg("bot", `**${label}** select kiya. Ab kaunsa subject?`, {
        action_type: "notes_picker",
        flow_step: "subject",
        options: subjects.map(s => s.label || s.value),
        option_values: subjects.map(s => s.value),
      });
    } catch {
      addBotMsg("bot", "Subjects fetch nahi ho paye. Please try again.", { action_type: "error" });
      setNotesFlow(prev => ({ ...prev, active: false }));
    } finally {
      setBotLoading(false);
    }
  }, []);

  const handleSubjectSelect = useCallback(async (value, label, msgIdx) => {
    markPickerHandled(msgIdx);
    addBotMsg("user", label);
    setBotLoading(true);
    setNotesFlow(prev => ({ ...prev, step: "loading_topics", subject: value }));
    try {
      const res = await axios.get(`${API}/notes/topics?exam_type=${notesFlow.category}&subject=${value}`);
      const topics = res.data.topics || [];
      setNotesFlow(prev => ({ ...prev, step: "topic", topics }));
      addBotMsg("bot", `**${label}** select kiya. Ab kaunsa topic?`, {
        action_type: "notes_picker",
        flow_step: "topic",
        options: topics.map(t => t.name || t),
        option_values: topics.map(t => t.name || t),
      });
    } catch {
      addBotMsg("bot", "Topics fetch nahi ho paye. Please try again.", { action_type: "error" });
      setNotesFlow(prev => ({ ...prev, active: false }));
    } finally {
      setBotLoading(false);
    }
  }, [notesFlow.category]);

  const handleTopicSelect = useCallback(async (topic, msgIdx) => {
    markPickerHandled(msgIdx);
    addBotMsg("user", topic);
    setBotLoading(true);
    setNotesFlow(prev => ({ ...prev, step: "fetching" }));
    addBotMsg("bot", `**${topic}** ke notes fetch kar raha hoon...`, { action_type: "notes_loading" });

    try {
      const { category, subject } = notesFlow;
      const hasPremium = user?.id && PAID_PLANS.includes(user?.plan);

      if (!user?.id) {
        addBotMsg("bot",
          "Notes access ke liye pehle **login karein**.\n\nLogin ke baad wapas Help bot mein notes request karein.",
          { action_type: "notes_login_required" }
        );
        setNotesFlow(prev => ({ ...prev, active: false, step: null }));
        setBotLoading(false);
        return;
      }

      if (!hasPremium) {
        addBotMsg("bot",
          "**Yeh notes premium members ke liye hai.**\n\n₹499 mein lifetime access lein — all subjects, all topics, Hindi + English.",
          {
            action_type: "notes_upgrade",
            enroll_url: `${window.location.origin}/enroll`,
          }
        );
        setNotesFlow(prev => ({ ...prev, active: false, step: null }));
        setBotLoading(false);
        return;
      }

      // Check if notes exist (fast)
      const statusRes = await axios.get(
        `${API}/chatbot/notes-status?exam_type=${category}&subject=${subject}&topic=${encodeURIComponent(topic)}`
      );
      const { exists } = statusRes.data;

      if (!exists) {
        // Trigger generation (background — don't wait)
        axios.get(
          `${API}/notes/get?exam_type=${category}&subject=${subject}&topic=${encodeURIComponent(topic)}&language=english&user_id=${user.id}`
        ).catch(() => {});
      }

      const pdfUrl = `${window.location.origin}/api/notes/pdf?exam_type=${category}&subject=${subject}&topic=${encodeURIComponent(topic)}&language=english`;
      const notesPageUrl = `${window.location.origin}/notes?exam_type=${category}&subject=${subject}&topic=${encodeURIComponent(topic)}`;

      addBotMsg("bot",
        exists
          ? `Yeh rahi **${topic}** ki notes! Neeche se PDF download karein ya notes page pe jayen:`
          : `**${topic}** ki notes generate ho rahi hain (~1-2 min). Aap seedha notes page pe bhi dekh sakte hain:`,
        {
          action_type: "notes_pdf",
          pdf_url: pdfUrl,
          notes_url: notesPageUrl,
          topic,
          exists,
        }
      );
    } catch {
      addBotMsg("bot", "Notes fetch nahi ho paya. Directly **Notes** section mein jayen.", { action_type: "error" });
    } finally {
      setNotesFlow(prev => ({ ...prev, active: false, step: null }));
      setBotLoading(false);
    }
  }, [notesFlow, user]);

  // ── Picker selection dispatcher ────────────────────────────────────────────
  const handlePickerSelect = useCallback((flowStep, value, label, msgIdx) => {
    if (flowStep === "category") handleCategorySelect(value, label, msgIdx);
    else if (flowStep === "subject") handleSubjectSelect(value, label, msgIdx);
    else if (flowStep === "topic") handleTopicSelect(value, msgIdx);
  }, [handleCategorySelect, handleSubjectSelect, handleTopicSelect]);

  // ── Bot send ───────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!input.trim() || botLoading) return;
    const userMessage = input.trim();
    setInput("");

    // Notes flow intent detection (frontend-side, no API needed)
    if (isNotesIntent(userMessage)) {
      addBotMsg("user", userMessage);
      startNotesFlow();
      return;
    }

    addBotMsg("user", userMessage);
    setBotLoading(true);

    try {
      const res = await axios.post(`${API}/student-support-bot/chat`, {
        message: userMessage,
        user_name: user?.name || "Student",
        user_phone: user?.mobile || "",
        user_email: user?.email || "",
        context: messages.slice(-10).map(m => ({ role: m.role === "bot" ? "assistant" : "user", content: m.content }))
      });
      const { reply, action_type, ask_field } = res.data;
      if (action_type === "escalated") {
        addBotMsg("bot", reply, { action_type: "escalated" });
        setEscalated(true);
      } else {
        addBotMsg("bot", reply, { action_type, ask_field });
      }
    } catch {
      addBotMsg("bot", "Kuch problem hua. Please call **8076623513** (9 AM–9 PM).", { action_type: "error" });
    } finally {
      setBotLoading(false);
    }
  };

  const quickActions = [
    { label: "Notes chahiye", msg: "Mujhe notes chahiye" },
    { label: "Access nahi mila", msg: "Maine payment kiya par access nahi mila" },
    { label: "Password bhool gaya", msg: "Mujhe password yaad nahi" },
    { label: "Mock test kholna hai", msg: "Full length mock test nahi khul raha" },
  ];

  const handleQuickAction = async (msg) => {
    if (botLoading) return;
    if (isNotesIntent(msg)) {
      addBotMsg("user", msg);
      startNotesFlow();
      return;
    }
    addBotMsg("user", msg);
    setBotLoading(true);
    try {
      const res = await axios.post(`${API}/student-support-bot/chat`, {
        message: msg,
        user_name: user?.name || "Student",
        user_phone: user?.mobile || "",
        user_email: user?.email || "",
        context: []
      });
      const { reply, action_type, ask_field } = res.data;
      if (action_type === "escalated") {
        addBotMsg("bot", reply, { action_type: "escalated" });
        setEscalated(true);
      } else {
        addBotMsg("bot", reply, { action_type, ask_field });
      }
    } catch {
      addBotMsg("bot", "Kuch problem hua. Please call **8076623513** (9 AM–9 PM).");
    } finally {
      setBotLoading(false);
    }
  };

  // ── Support ticket form submit ─────────────────────────────────────────────
  const handleSubmitTicket = async () => {
    if (!formMessage.trim()) return;
    setFormSubmitting(true);
    try {
      await axios.post(`${API}/support/submit-ticket`, {
        name:    user?.name || "Student",
        phone:   user?.mobile || "",
        email:   user?.email || "",
        message: formMessage.trim(),
        user_id: user?.id || user?._id || "",
      });
      setFormSubmitted(true);
      setFormMessage("");
    } catch {
      alert("Ticket submit nahi hua. Please call 8076623513.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const currentHour = new Date().getHours();
  const isSupportAvailable = currentHour >= 9 && currentHour < 21;

  // ── Collapsed trigger button ───────────────────────────────────────────────
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="chatbot-open-btn"
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-0 hover:gap-2.5 bg-gray-900 text-white pl-3.5 pr-3.5 hover:pr-5 py-3 rounded-full shadow-2xl shadow-black/40 border border-white/10 hover:bg-gray-800 transition-all duration-300 overflow-hidden"
        style={{ minWidth: "52px" }}
      >
        <span className="relative flex-shrink-0">
          <span className="block w-2.5 h-2.5 bg-emerald-400 rounded-full"></span>
          <span className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-60"></span>
        </span>
        <span className="text-sm font-bold tracking-wide text-white ml-2.5 flex-shrink-0">Help?</span>
        <span className="text-xs text-gray-300 whitespace-nowrap overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-300 ml-0 group-hover:ml-1">
          Lokesh Sir Team
        </span>
      </button>
    );
  }

  // ── Chat window ────────────────────────────────────────────────────────────
  return (
    <div
      className={`fixed bottom-6 right-4 sm:right-6 z-[70] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${isMinimized ? "w-72 h-14" : "w-[calc(100vw-2rem)] sm:w-80 md:w-96 max-h-[90vh] sm:h-[570px]"}`}
      data-testid="chatbot-container"
    >
      {/* Header */}
      <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-sm font-bold text-white shadow">
              L
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-gray-900"></span>
          </div>
          <div>
            <h3 className="font-bold text-sm leading-tight">Lokesh Sir Team</h3>
            <p className="text-[11px] text-gray-400 leading-tight">
              {activeTab === "bot" ? "AI Support" : "Report Issue"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chatbot-minimize-btn">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chatbot-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Tab switcher */}
          <div className="flex border-b border-gray-100 bg-white">
            <button
              onClick={() => setActiveTab("bot")}
              data-testid="tab-bot"
              className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${activeTab === "bot" ? "text-gray-900 border-b-2 border-gray-900 bg-gray-50" : "text-gray-400 hover:text-gray-600"}`}
            >
              <HelpCircle className="w-3.5 h-3.5" />
              Help? Ask Bot
            </button>
            <button
              onClick={() => setActiveTab("form")}
              data-testid="tab-form"
              className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${activeTab === "form" ? "text-gray-900 border-b-2 border-gray-900 bg-gray-50" : "text-gray-400 hover:text-gray-600"}`}
            >
              <AlertCircle className="w-3.5 h-3.5" />
              Report Issue
            </button>
          </div>

          {/* ── BOT TAB ─────────────────────────────────────────────────────── */}
          {activeTab === "bot" && (
            <div className="flex flex-col h-[calc(570px-96px)]">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
                {messages.map((msg, idx) => {
                  const isBot = msg.role === "bot";
                  const isEscalated = msg.action_type === "escalated";
                  const isAdminReply = msg.action_type === "admin_reply";
                  const isPicker = msg.action_type === "notes_picker";
                  const isPdf = msg.action_type === "notes_pdf";
                  const isUpgrade = msg.action_type === "notes_upgrade";

                  return (
                    <div key={idx} className={`flex ${isBot ? "justify-start" : "justify-end"}`}>
                      {isBot && (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[10px] font-bold text-white mr-1.5 mt-0.5 flex-shrink-0">
                          L
                        </div>
                      )}
                      <div className="max-w-[85%]">
                        <div
                          className={`px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                            isBot
                              ? isEscalated  ? "bg-amber-50 border border-amber-200 text-amber-900 rounded-bl-sm"
                                : isAdminReply ? "bg-blue-50 border border-blue-200 text-blue-900 rounded-bl-sm"
                                : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm"
                              : "bg-gray-900 text-white rounded-br-sm"
                          }`}
                        >
                          {isAdminReply && (
                            <div className="flex items-center gap-1.5 mb-1.5 text-blue-700">
                              <MessageSquareReply className="w-3.5 h-3.5" />
                              <span className="text-[11px] font-semibold uppercase tracking-wide">Admin Reply</span>
                            </div>
                          )}
                          {isEscalated && (
                            <div className="flex items-center gap-1.5 mb-1.5 text-amber-700">
                              <AlertCircle className="w-3.5 h-3.5" />
                              <span className="text-[11px] font-semibold uppercase tracking-wide">Escalated to Care Team</span>
                            </div>
                          )}
                          <MessageText text={msg.content} />

                          {/* Escalation CTA */}
                          {isEscalated && (
                            <div className="mt-2 pt-2 border-t border-amber-200">
                              <p className="text-[11px] text-amber-700 mb-1.5">Apna issue form mein submit karein — team contact karegi.</p>
                              <button
                                onClick={() => setActiveTab("form")}
                                className="text-[11px] bg-amber-600 text-white px-3 py-1 rounded-full hover:bg-amber-700 transition-colors"
                                data-testid="escalated-form-btn"
                              >
                                Submit Support Request
                              </button>
                            </div>
                          )}

                          {/* PDF delivery */}
                          {isPdf && (
                            <div className="mt-2.5 pt-2 border-t border-gray-200 space-y-1.5">
                              <a
                                href={msg.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-indigo-600 text-white text-xs px-3 py-2 rounded-lg hover:bg-indigo-500 transition-colors"
                                data-testid={`notes-pdf-link-${idx}`}
                              >
                                <Download className="w-3.5 h-3.5 shrink-0" />
                                Download PDF — {msg.topic}
                              </a>
                              <a
                                href={msg.notes_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-[11px] text-indigo-600 hover:text-indigo-500 transition-colors"
                              >
                                <BookOpen className="w-3 h-3" />
                                Notes page pe dekhen
                                <ChevronRight className="w-3 h-3" />
                              </a>
                            </div>
                          )}

                          {/* Upgrade CTA */}
                          {isUpgrade && (
                            <div className="mt-2.5 pt-2 border-t border-gray-200">
                              <a
                                href={msg.enroll_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs px-3 py-2 rounded-lg hover:from-indigo-500 hover:to-purple-500 transition-all"
                                data-testid="notes-upgrade-btn"
                              >
                                Enroll Now — ₹499 Lifetime Access
                                <ChevronRight className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Notes picker buttons (shown below the bubble) */}
                        {isPicker && !msg.handled && (
                          <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                            {(msg.options || []).map((opt, oi) => (
                              <button
                                key={oi}
                                onClick={() => handlePickerSelect(msg.flow_step, msg.option_values[oi], opt, idx)}
                                disabled={botLoading}
                                className="text-[11px] bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1.5 rounded-lg hover:bg-indigo-50 hover:border-indigo-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid={`picker-${msg.flow_step}-${oi}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                        {isPicker && msg.handled && (
                          <p className="text-[10px] text-gray-400 mt-1 ml-1">Selected above</p>
                        )}
                      </div>
                    </div>
                  );
                })}

                {botLoading && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[10px] font-bold text-white mr-1.5 mt-0.5 flex-shrink-0">
                      L
                    </div>
                    <div className="bg-white border border-gray-200 px-3.5 py-3 rounded-2xl rounded-bl-sm shadow-sm flex items-center gap-2">
                      <span className="flex gap-1">
                        {[0, 150, 300].map(d => (
                          <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                        ))}
                      </span>
                    </div>
                  </div>
                )}
                <div ref={botEndRef} />
              </div>

              {/* Quick actions (only at start) */}
              {messages.length <= 1 && (
                <div className="px-3 py-2 border-t border-gray-100 bg-white">
                  <p className="text-[10px] text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Quick Help</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {quickActions.map((a, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickAction(a.msg)}
                        disabled={botLoading}
                        className="text-[11px] bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 px-2 py-2 rounded-xl text-left transition-colors leading-tight disabled:opacity-50"
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Status bar */}
              <div className={`px-3 py-1.5 text-[11px] flex items-center justify-center gap-1.5 ${isSupportAvailable ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-600"}`}>
                <Clock className="w-3 h-3" />
                {isSupportAvailable ? "Team online · Call 8076623513" : "Offline now · Call 8076623513 (9 AM–9 PM)"}
              </div>

              {/* Input */}
              <div className="p-3 border-t border-gray-200 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyPress={e => e.key === "Enter" && !botLoading && handleSend()}
                    placeholder={notesFlow.active ? "Category/subject/topic chunein upar se..." : "Apna sawal likhein..."}
                    disabled={botLoading || escalated}
                    className="flex-1 text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:bg-gray-50"
                    data-testid="chatbot-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={botLoading || !input.trim() || escalated}
                    className="bg-gray-900 hover:bg-gray-800 rounded-xl px-4 disabled:opacity-40"
                    data-testid="chatbot-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                {escalated && (
                  <p className="text-[11px] text-amber-600 mt-1.5 text-center">
                    Query escalate ho gayi.{" "}
                    <button onClick={() => setActiveTab("form")} className="underline font-semibold">Submit Request</button>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── FORM TAB ────────────────────────────────────────────────────── */}
          {activeTab === "form" && (
            <div className="flex flex-col h-[calc(570px-96px)] bg-gray-50">
              <div className="flex-1 overflow-y-auto p-4">
                {formSubmitted ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <CheckCircle2 className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">Request Submitted!</h3>
                    <p className="text-sm text-gray-500 mb-1">Hamari team aapko jald hi contact karegi.</p>
                    <p className="text-xs text-gray-400 mb-4">Lokesh sir ka jawab Help bot mein dikhega.</p>
                    <button
                      onClick={() => { setFormSubmitted(false); setActiveTab("bot"); }}
                      className="text-xs bg-gray-900 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors"
                      data-testid="form-back-btn"
                    >
                      Back to Help Bot
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">Contact Support Team</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Bot resolve nahi kar paya? Yeh form bharo — team personally contact karegi aur Help bot mein reply milega.</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Phone Number</label>
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
                        <span className="text-gray-400 text-sm select-none">📞</span>
                        <span className="text-sm text-gray-700 font-medium flex-1" data-testid="form-phone">
                          {user?.mobile || "Not available"}
                        </span>
                        {user?.mobile && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full font-medium">Auto</span>}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Email Address</label>
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
                        <span className="text-gray-400 text-sm select-none">✉️</span>
                        <span className="text-sm text-gray-700 font-medium flex-1 truncate" data-testid="form-email">
                          {user?.email || "Not available"}
                        </span>
                        {user?.email && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full font-medium">Auto</span>}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">How can we help you? *</label>
                      <textarea
                        value={formMessage}
                        onChange={e => setFormMessage(e.target.value)}
                        placeholder="Apni problem detail mein likhein..."
                        rows={5}
                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 resize-none"
                        data-testid="form-message-input"
                      />
                    </div>
                    <button
                      onClick={handleSubmitTicket}
                      disabled={formSubmitting || !formMessage.trim()}
                      className="w-full bg-gray-900 text-white text-sm font-semibold py-3 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                      data-testid="form-submit-btn"
                    >
                      {formSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Support Request</>}
                    </button>
                    <p className="text-[11px] text-gray-400 text-center">
                      Admin ka reply aapko Help bot tab mein milega jab aap wapas open karein.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
